class TranslationService {
  constructor() {
    this.cache = new Map();
    this.activeCalls = new Map();
    this.preloadedCommon = false;
  }

  static getInstance() {
    if (!TranslationService.instance) {
      TranslationService.instance = new TranslationService();
    }
    return TranslationService.instance;
  }

  static async translate(text, fromLang, toLang) {
    const instance = this.getInstance();
    return instance.translateText(text, fromLang, toLang);
  }

  async translateText(text, fromLang, toLang) {
    if (!text || !text.trim()) {
      return {
        translatedText: '',
        detectedLanguage: fromLang === 'auto' ? 'en' : fromLang
      };
    }

    const normalizedText = text.trim();
    
    // DETECCIÓN DE IDIOMA ULTRA-PRECISA
    let actualSourceLang = fromLang;
    let detectedLang = null;

    if (fromLang === 'auto') {
      detectedLang = this.detectLanguageAdvanced(normalizedText);
      actualSourceLang = detectedLang;
    }

    if (actualSourceLang === toLang && actualSourceLang !== 'auto') {
      return {
        translatedText: normalizedText,
        detectedLanguage: detectedLang || actualSourceLang
      };
    }

    const cacheKey = `${normalizedText}|${actualSourceLang}|${toLang}`;
    
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);
    if (this.activeCalls.has(cacheKey)) return this.activeCalls.get(cacheKey);

    const translationPromise = this.performTranslation(normalizedText, actualSourceLang, toLang, detectedLang);
    this.activeCalls.set(cacheKey, translationPromise);

    try {
      const result = await translationPromise;
      this.cache.set(cacheKey, result);
      return result;
    } finally {
      this.activeCalls.delete(cacheKey);
    }
  }

  detectLanguageAdvanced(text) {
    if (!text || text.length < 2) return 'en';
    const cleanText = text.toLowerCase().trim();

    // 1. Caracteres únicos (99% precisión para alfabetos no latinos)
    const unique = this.detectByUniqueCharacters(cleanText);
    if (unique.confidence > 0.8) return unique.language;

    // 2. Patrones de palabras (Artículos, preposiciones)
    const patterns = this.detectByWordPatterns(cleanText);
    if (patterns.confidence > 0.7) return patterns.language;

    // 3. N-Grams (Bigramas y Trigramas)
    const ngrams = this.detectByNGrams(cleanText);
    if (ngrams.confidence > 0.6) return ngrams.language;

    // 4. Estadísticas (Vocales/Consonantes)
    return this.detectByStatistics(cleanText).language || 'en';
  }

  detectByUniqueCharacters(text) {
    const uniqueCharacters = {
      'es': { chars: /[ñáéíóúü¿¡]/g, weight: 10, min: 1 },
      'fr': { chars: /[àâäéèêëïîôöùûüÿç]/g, weight: 8, min: 1 },
      'de': { chars: /[äöüßÄÖÜ]/g, weight: 9, min: 1 },
      'pt': { chars: /[ãõáâàéêíóôúç]/g, weight: 8, min: 1 },
      'ru': { chars: /[а-яёЁ]/g, weight: 10, min: 2 },
      'zh': { chars: /[\u4e00-\u9fff]/g, weight: 10, min: 1 },
      'ja': { chars: /[\u3040-\u309f\u30a0-\u30ff]/g, weight: 10, min: 1 },
      'ko': { chars: /[\uac00-\ud7af]/g, weight: 10, min: 1 },
      'ar': { chars: /[\u0600-\u06ff]/g, weight: 10, min: 1 },
      'hi': { chars: /[\u0900-\u097f]/g, weight: 10, min: 1 }
    };

    let best = { language: 'en', confidence: 0 };
    for (const [lang, config] of Object.entries(uniqueCharacters)) {
      const matches = (text.match(config.chars) || []).length;
      if (matches >= config.min) {
        const conf = Math.min(0.95, (matches * config.weight) / Math.max(text.length / 10, 5));
        if (conf > best.confidence) best = { language: lang, confidence: conf };
      }
    }
    return best;
  }

  detectByWordPatterns(text) {
    const wordPatterns = {
      'es': { patterns: [/\b(el|la|los|las|un|una|de|en|con|por|para|que|como|hola|gracias)\b/g], weight: 5 },
      'en': { patterns: [/\b(the|a|an|in|on|at|by|for|with|and|but|hello|thanks)\b/g], weight: 4 },
      'fr': { patterns: [/\b(le|la|les|un|une|des|de|en|dans|sur|pour|et|mais|bonjour|merci)\b/g], weight: 5 },
      'de': { patterns: [/\b(der|die|das|ein|eine|in|an|auf|bei|mit|von|und|oder|hallo|danke)\b/g], weight: 5 },
      'it': { patterns: [/\b(il|la|lo|gli|le|un|una|di|a|in|con|da|per|e|o|ciao|grazie)\b/g], weight: 5 },
      'pt': { patterns: [/\b(o|a|os|as|um|uma|de|em|com|por|para|e|ou|olá|obrigado)\b/g], weight: 5 }
    };

    let best = { language: 'en', confidence: 0 };
    for (const [lang, config] of Object.entries(wordPatterns)) {
      let total = 0;
      config.patterns.forEach(p => total += (text.match(p) || []).length);
      const conf = Math.min(0.9, (total * config.weight) / Math.max(text.split(/\s+/).length, 5));
      if (conf > best.confidence) best = { language: lang, confidence: conf };
    }
    return best;
  }

  detectByNGrams(text) {
    const ngrams = {
      'es': { tri: ['que', 'ent', 'ion', 'aci', 'est'], bi: ['de', 'en', 'el', 'la', 'es'] },
      'en': { tri: ['the', 'and', 'ing', 'ion', 'ent'], bi: ['th', 'he', 'in', 'er', 're'] },
      'fr': { tri: ['les', 'des', 'que', 'ent', 'ion'], bi: ['le', 'de', 'et', 'en', 'es'] },
      'de': { tri: ['der', 'die', 'und', 'ich', 'den'], bi: ['en', 'er', 'ch', 'nd', 'te'] }
    };

    let best = { language: 'en', confidence: 0 };
    for (const [lang, config] of Object.entries(ngrams)) {
      let score = 0;
      config.tri.forEach(t => score += (text.match(new RegExp(t, 'g')) || []).length * 3);
      config.bi.forEach(b => score += (text.match(new RegExp(b, 'g')) || []).length * 1.5);
      const conf = Math.min(0.8, (score * 3) / Math.max(text.length / 5, 10));
      if (conf > best.confidence) best = { language: lang, confidence: conf };
    }
    return best;
  }

  detectByStatistics(text) {
    const vowels = (text.match(/[aeiouáéíóúàèìòùâêîôûäëïöüãõ]/gi) || []).length;
    const ratio = vowels / text.replace(/\s+/g, '').length;
    
    if (ratio > 0.43) return { language: 'it', confidence: 0.5 };
    if (ratio > 0.40) return { language: 'es', confidence: 0.5 };
    if (ratio < 0.38) return { language: 'en', confidence: 0.5 };
    
    return { language: 'en', confidence: 0.1 };
  }

  async performTranslation(text, fromLang, toLang, detectedLang) {
    // Intentar MyMemory primero (muy estable)
    try {
      const res = await this.tryMyMemoryTranslate(text, fromLang, toLang);
      if (res?.translatedText) return res;
    } catch (e) {}

    // Intentar Google gtx (alternativa robusta)
    try {
      const res = await this.tryGoogleTranslate(text, fromLang, toLang);
      if (res?.translatedText) return res;
    } catch (e) {}

    // Intentar LibreTranslate (backup)
    try {
      const res = await this.tryLibreTranslate(text, fromLang, toLang);
      if (res?.translatedText) return res;
    } catch (e) {}

    return this.getFallbackTranslation(text, fromLang, toLang, detectedLang);
  }

  async tryGoogleTranslate(text, fromLang, toLang) {
    const sl = fromLang === 'auto' ? 'auto' : fromLang;
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sl}&tl=${toLang}&dt=t&q=${encodeURIComponent(text)}`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      let translated = "";
      if (data[0]) data[0].forEach(s => translated += s[0]);
      return { translatedText: translated, detectedLanguage: data[2] || fromLang };
    }
    throw new Error();
  }

  async tryMyMemoryTranslate(text, fromLang, toLang) {
    const pair = `${fromLang === 'auto' ? '' : fromLang}|${toLang}`;
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${pair}`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      if (data.responseStatus === 200) {
        return { translatedText: data.responseData.translatedText, detectedLanguage: fromLang };
      }
    }
    throw new Error();
  }

  async tryLibreTranslate(text, fromLang, toLang) {
    const response = await fetch('https://translate.argosopentech.com/translate', {
      method: 'POST',
      body: JSON.stringify({ q: text, source: fromLang === 'auto' ? 'auto' : fromLang, target: toLang, format: 'text' }),
      headers: { 'Content-Type': 'application/json' }
    });
    if (response.ok) {
      const data = await response.json();
      return { translatedText: data.translatedText, detectedLanguage: data.detectedLanguage || fromLang };
    }
    throw new Error();
  }

  getFallbackTranslation(text, fromLang, toLang, detectedLang) {
    return { 
      translatedText: text, 
      detectedLanguage: detectedLang || this.detectLanguageAdvanced(text) 
    };
  }

  static async preloadCommonTranslations() {
    // Opcional: pre-cachear frases comunes
  }

  static detectLanguageFromText(text) {
    return this.getInstance().detectLanguageAdvanced(text);
  }
}

export default TranslationService;